<template>
    <div
        ref="scrollyContainer"
        v-resize:debounce="onResize"
        class="scrolly-page tab-container"
    >
        <div
            :id="content.domId"
            class="scroll-placeholder"
        />
        <h1 class="tab-header">
            <div>{{ content.title }}</div>
        </h1>
        <div class="header-main-content tab-description text-block">
            {{ content.description }}
        </div>
        <div
            class="print-graphic"
        >
            <img
                v-for="category in content.categories"
                :key="`print-main-image-${category.id}`"
                :src="graphicUrl(category)"
                class="sticky-main-image"
            >
        </div>
        <template v-if="useMobile || isIE">
            <div
                v-for="(category, i) in content.categories"
                :key="`mobile-${category.id}`"
                class="mobile-graphic"
            >
                <img
                    v-for="j in i + 1"
                    ref="mobileMainImage"
                    :key="j"
                    :src="graphicUrl(content.categories[j - 1])"
                    :style="{ marginTop: `${j <= 1 ? 0 : -imgOffset}px` }"
                    class="sticky-main-image"
                >
                <div
                    ref="stickyExpandedImage"
                    class="sticky-expanded-image"
                    :style="expandedImageStyles[category.id]"
                >
                    <span
                        v-for="(detail, i) in category.details"
                        :key="i"
                        :class="category.colorClass || detail.colorClass"
                        class="detail-icon-container"
                    >
                        <svg-inline :src="detail.icon" />
                        <div class="text-neutral-1 detail-title">{{ detail.title }}</div>
                    </span>
                </div>
                <div
                    :id="category.domId"
                    :key="`description-${category.id}`"
                    class="scrolly-section-description text-block"
                >
                    <p v-html="category.description" />
                </div>
            </div>
        </template>
        <template
            v-for="category in content.categories"
            v-else
        >
            <div
                :key="`graphic-${category.id}`"
                ref="stickyGraphic"
                class="sticky-graphic"
                :style="stickyGraphicStyles[category.id]"
            >
                <img
                    ref="stickyMainImage"
                    :src="graphicUrl(category)"
                    :style="stickyMainImageStyles[category.id]"
                    class="sticky-main-image"
                >
                <div
                    ref="stickyExpandedImage"
                    class="sticky-expanded-image"
                    :style="expandedImageStyles[category.id]"
                >
                    <span
                        v-for="(detail, i) in category.details"
                        :key="i"
                        :class="category.colorClass || detail.colorClass"
                        class="detail-icon-container"
                    >
                        <!-- NOTE: ColourClass above: To use the detail.colorClass instead
                                of the category colour class, just need to reverse the order
                                of the :class condition above -->
                        <svg-inline :src="detail.icon" />
                        <div class="text-neutral-1 detail-title">{{ detail.title }}</div>
                    </span>
                </div>
            </div>
            <div
                :id="category.domId"
                :key="`description-${category.id}`"
                class="scrolly-section-description text-block"
            >
                <p v-html="category.description" />
            </div>
        </template>
    </div>
</template>

<script>
import resize from 'vue-resize-directive';
import _ from 'lodash';
import { isIE } from '@/services/helpers';
import SvgInline from '@/components/SvgInline.vue';

export default {
    components: {
        SvgInline
    },
    directives: {
        resize
    },
    props: {
        content: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            // Positions that computeds need to react to
            mainHeights: [],
            expandedHeights: [],
            mainTops: [],
            expandedTops: [],
            imgOffset: 0
        };
    },
    computed: {
        top() {
            return this.$screens.header + 80;
        },
        isIE() {
            return isIE();
        },
        useMobile() {
            return this.isIE || this.$screens.mobile;
        },
        stickyGraphicStyles() {
            // Sets the top, which determines where each graphic is sticky
            // minHeight needed so that all the graphics scroll out together
            const styles = _.map(this.stickyPositions, (position) => {
                const minHeight = `${position.minHeight}px`;
                return {
                    id: position.id,
                    top: `${position.top}px`,
                    minHeight
                };
            });
            return _.keyBy(styles, 'id');
        },
        stickyMainImageStyles() {
            // Margin top to overlat the images a bit.
            const styles = _.map(this.stickyPositions, (position) => ({
                id: position.id,
                marginTop: `${-position.overlap}px`
            }));
            return _.keyBy(styles, 'id');
        },
        expandedImageStyles() {
            // Opacity for the expanded image to fade out, based on positions.
            const styles = _.map(this.content.categories, (category, i) => {
                const expHeight = this.expandedHeights[i];
                const expTop = this.expandedTops[i];
                const mainTop = this.mainTops[i + 1];
                const opacity = (mainTop) ? (mainTop - expTop) / expHeight : 1;
                const style = { id: category.id, opacity };
                return style;
            });
            return _.keyBy(styles, 'id');
        },
        visibleHeight() {
            const mainHeights = _.sum(this.mainHeights);
            // Last image the expanded is always visible.
            const imageHeight = mainHeights + _.last(this.expandedHeights);
            // TODO: 20 is for margin, should be calculated
            return imageHeight;
        },
        // Based on the main image heights, the sticky positions are computed
        stickyPositions() {
            if (this.useMobile) {
                return {};
            }
            const stickyPositions = _.reduce(this.mainHeights, (positions, height, i) => {
                const prev = positions[i - 1];
                // Some magic numbers here to position images
                // TODO: Might need to change if images are changed
                const border = 8;
                const expHeight = this.expandedHeights[i];
                const totalHeight = height + expHeight;
                const borderedHeight = height + border;
                const overlap = (i) ? this.imgOffset : 0;
                const top = (i) ? prev.top + prev.height - prev.overlap : this.top;
                // Visible Height is the height of the images combined
                // Subtract top to get height needed for this image
                // Add this.top to make it relative to the scrolly
                // this.top is the buffer needed for the header at the top for the scroll to start pushing
                const minHeight = (this.visibleHeight - top) + this.top;
                const pos = {
                    id: this.content.categories[i].id,
                    top,
                    overlap,
                    height: borderedHeight,
                    minHeight,
                    totalHeight,
                    isLast: i === (this.mainHeights.length - 1),
                    index: i
                };

                positions.push(pos);
                return positions;
            }, []);
            return _.keyBy(stickyPositions, 'id');
        }
    },
    created() {
        window.addEventListener('scroll', this.onScroll);
    },
    destroyed() {
        window.removeEventListener('scroll', this.onScroll);
    },
    methods: {
        // When page scrolls, the image positions are set in data for the computed styles to react to
        async onScroll() {
            if (this.useMobile) {
                return;
            }
            await this.$nextTick();
            for (let i = 0; i < this.$refs.stickyMainImage.length; i++) {
                const mainImg = this.$refs.stickyMainImage[i];
                const expImg = this.$refs.stickyExpandedImage[i];
                this.$set(this.mainTops, i, mainImg.getBoundingClientRect().top);
                this.$set(this.expandedTops, i, expImg.getBoundingClientRect().top);
            }
        },
        // When page resizes, images resize responsively
        // Sets the heights of each image in the data so the styles will
        // update reactively to position the sticky images.
        async onResize() {
            await this.$nextTick();
            const before = this.$el.getBoundingClientRect().height;

            let imgHeight = 0;
            if (this.useMobile) {
                const mobileMainImg = this.$refs.mobileMainImage[0];
                imgHeight = mobileMainImg.offsetHeight;
            } else {
                for (let i = 0; i < this.$refs.stickyMainImage.length; i++) {
                    const mainImg = this.$refs.stickyMainImage[i];

                    if (!this.mainHeights[i] || Math.abs(this.mainHeights[i] - mainImg.offsetHeight) > 1) {
                        this.$set(this.mainHeights, i, mainImg.offsetHeight);
                    }
                    if (!this.useMobile) {
                        const expImg = this.$refs.stickyExpandedImage[i];
                        if (!this.expandedHeights[i] || Math.abs(this.expandedHeights[i] - expImg.offsetHeight) > 1) {
                            this.$set(this.expandedHeights, i, expImg.offsetHeight);
                        }
                    }
                }
                imgHeight = this.mainHeights[0];
            }
            this.imgOffset = (imgHeight + 8) * 0.25;

            await this.$nextTick();
            const after = this.$el.getBoundingClientRect().height;
            if (after !== before) {
                this.$emit('resize', { before, after, diff: after - before });
            }
        },
        graphicUrl(category) {
            // eslint-disable-next-line import/no-dynamic-require, global-require
            return require(`@/assets/${category.image}.png`);
        }
    }
};
</script>

<style src="./common.tab.css" scoped></style>
<style src="./common.section.css" scoped></style>
<style lang="postcss" scoped>
.scrolly-page {
    /* min-height: 100vh; */
}

.print-graphic {
    display: none;
}
.header-main-content {
    margin-bottom: 70px;
    /* width: 50%; */
}
.detail-title {
    @apply w-4/5 text-center mx-auto;
    margin-top: -8%
}

.scrolly-page-title {
    @apply sticky top-header;
}
.sticky-graphic {
    /* @apply border rounded-lg border-solid border-gray-600; */
    @apply sticky w-1/2 inline-block text-center;
    background: rgba(255,255,255,.8);
    box-shadow: 0 0 15px 10px rgba(255,255,255,.8);
}
.mobile-graphic {
    @apply block text-center;
    max-width: 600px;
}
.sticky-expanded-image {
    padding-top: 30px;
}
.sticky-main-image {
}
.scrolly-section-description {
    margin-left: 5%;
    width: 40%;
    display: inline-block;
    vertical-align: top;
    margin-top: 30px;
    min-height: 60vh;
    text-align: left;
}
.detail-icon-old {
    /* @apply border rounded-lg border-solid border-gray-600; */
    min-width: 33%;
    margin: 0;
    display: inline-block;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 12px;
}
.detail-icon-container {
    /* @apply border rounded-lg border-solid border-gray-600; */
    @apply w-1/3 my-2;
    min-width: 33%;
    display: inline-block;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 12px;
    vertical-align: top;
}

.svg-inline-container {
    @apply w-2/3;
}

.svg-inline-container {
    @apply w-2/3;
}

span.header-icon {
    margin: 0;
    margin-right: 0;
}

/* Media breakpoints last if we want them to override defaults */
@screen mobile {
    .scrolly-section-description {
        @apply w-full relative mx-0 mb-2;
        min-height: 0;
        /* margin-left: -100px; */
        /* background-color: red; */
        /* min-height: 0 !important; */
    }
}

@media all and (-ms-high-contrast:none), (-ms-high-contrast: active) {
    .scrolly-section-description {
        @apply w-full relative mx-0 mb-2;
        min-height: 0;
        /* margin-left: -100px; */
        /* background-color: red; */
        /* min-height: 0 !important; */
    }
}

</style>
<style lang="postcss">
.svg-inline .cls-1{
    fill: currentColor;
}
</style>
